import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  ArrowLeft, Search, Loader2, X, ChevronRight, Sparkles,
  Plus, Trash2, Save, CheckCircle2, AlertCircle, ClipboardCheck, CheckCheck,
} from "lucide-react";
import { toast } from "sonner";

import { LOGO_URL } from "../config/constants";

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

// ========== STEP 1: Matter selection ==========

function StepMatterSelection({ onSelect }) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [explanation, setExplanation] = useState("");

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/hub/ai-search`, { query: query.trim(), limit: 10 });
      setResults(res.data.results || []);
      setExplanation(res.data.explanation || "");
      if (!res.data.results?.length) toast.info("No matters found.");
    } catch {
      toast.error("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="glass-card p-6">
        <h2 className="text-lg font-bold text-teal mb-1">Step 1 — Select Matter</h2>
        <p className="text-sm text-txt-muted mb-5">Search for the matter this checklist relates to.</p>

        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. 'VEC0089', 'Smith purchase'"
              className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted pr-8"
              data-testid="pc-matter-search-input"
            />
            {query && (
              <button type="button" onClick={() => { setQuery(""); setResults(null); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-txt-muted hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button type="submit" disabled={searching || !query.trim()} className="bg-teal hover:bg-teal-hover text-white gap-1.5" data-testid="pc-matter-search-btn">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </Button>
        </form>

        <div className="mb-4 text-center">
          <button onClick={() => onSelect(null)} className="text-sm text-teal hover:text-teal underline" data-testid="pc-skip-matter-btn">
            Or enter details manually
          </button>
        </div>

        {results !== null && (
          <div>
            {explanation && (
              <p className="text-xs text-teal mb-3 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> {explanation}
              </p>
            )}
            {results.length > 0 ? (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {results.map((r, i) => (
                  <button
                    key={r.id || i}
                    onClick={() => onSelect(r)}
                    className="w-full glass-card p-3 rounded-md text-left flex items-center justify-between hover:border-teal/40 transition-colors group"
                    data-testid={`pc-matter-result-${i}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{r.secondary_label}</p>
                      <p className="text-sm text-txt-muted truncate">{r.primary_label}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-txt-muted group-hover:text-teal group-hover:translate-x-1 transition-all flex-shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-txt-muted py-4">No results for "{query}".</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ========== STEP 2: Checklist Form ==========

function StepChecklist({ initialMatter, onBack, existingId }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const [matterReference, setMatterReference] = useState("");
  const [matterId, setMatterId] = useState("");
  const [clientName, setClientName] = useState("");
  const [counterpartyName, setCounterpartyName] = useState("");
  const [matterDescription, setMatterDescription] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [feeEarner, setFeeEarner] = useState("");

  const [tasks, setTasks] = useState([]);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  useEffect(() => {
    if (existingId) {
      axios.get(`${API}/postcompletion/${existingId}`).then((res) => {
        const d = res.data.data || {};
        const h = d.header || {};
        setMatterReference(h.matter_reference || "");
        setMatterId(h.matter_id || "");
        setClientName(h.client_name || "");
        setCounterpartyName(h.counterparty_name || "");
        setMatterDescription(h.matter_description || "");
        setTransactionType(h.transaction_type || "");
        setCompletionDate(h.completion_date || "");
        setFeeEarner(h.fee_earner || "");
        setTasks(d.tasks || []);
      }).catch(() => {});
      return;
    }

    if (!initialMatter) {
      loadDefaults("property");
      return;
    }

    const matterRef = initialMatter.secondary_label || initialMatter.matter_reference || "";
    setMatterId(initialMatter.id || "");
    if (initialMatter.primary_label) setClientName(initialMatter.primary_label);

    if (matterRef) {
      axios.get(`${API}/completion-statement/prefill/${encodeURIComponent(matterRef)}`).then((res) => {
        if (res.data.found) {
          const p = res.data.prefill;
          setMatterReference(p.matter_reference || matterRef);
          setMatterId(p.matter_id || initialMatter.id || "");
          setClientName(p.client_name || "");
          setCounterpartyName(p.counterparty_name || "");
          setTransactionType(p.transaction_type || "");
          setFeeEarner(p.fee_earner || "");
          // BM-H4191: the client type picks the company task list for a
          // company remortgage or transfer of equity.
          loadDefaults(p.transaction_type || p.specific_matter_type || "", p.client_type || "");
          toast.success("Header pre-populated from matter data");
        } else {
          setMatterReference(matterRef);
          loadDefaults("");
        }
      }).catch(() => {
        setMatterReference(matterRef);
        loadDefaults("");
      });
    } else {
      loadDefaults("");
    }
  }, [initialMatter, existingId]);

  const loadDefaults = useCallback(async (tt, clientType = "") => {
    setLoadingDefaults(true);
    try {
      const res = await axios.get(`${API}/postcompletion/defaults/${encodeURIComponent(tt || "property")}`,
        clientType ? { params: { client_type: clientType } } : undefined);
      setTasks(res.data.tasks || []);
    } catch {
      setTasks([{ id: uid(), category: "", description: "", due_date: "", completed: false, completed_at: "", responsible: "", notes: "", document_link: "", sync_to_todo: false, todo_task_id: "", todo_list_id: "", todo_synced_user: "" }]);
    } finally {
      setLoadingDefaults(false);
    }
  }, []);

  const addTask = () => {
    setTasks([...tasks, { id: uid(), category: "", description: "", due_date: "", completed: false, completed_at: "", responsible: "", notes: "", document_link: "", sync_to_todo: false, todo_task_id: "", todo_list_id: "", todo_synced_user: "" }]);
  };

  const removeTask = (idx) => setTasks(tasks.filter((_, i) => i !== idx));

  const updateTask = (idx, field, value) => {
    const copy = [...tasks];
    copy[idx] = { ...copy[idx], [field]: value };
    if (field === "completed" && value === true) {
      copy[idx].completed_at = new Date().toISOString();
    }
    setTasks(copy);
  };

  const markAllComplete = () => {
    const now = new Date().toISOString();
    setTasks(tasks.map((t) => ({ ...t, completed: true, completed_at: t.completed_at || now })));
  };

  const completedCount = tasks.filter((t) => t.completed).length;
  const totalCount = tasks.length;
  const allDone = totalCount > 0 && completedCount === totalCount;

  const handleSave = async () => {
    setSaving(true);
    setErrors([]);
    const payload = {
      header: {
        matter_reference: matterReference,
        matter_id: matterId,
        client_name: clientName,
        counterparty_name: counterpartyName,
        matter_description: matterDescription,
        transaction_type: transactionType,
        completion_date: completionDate,
        fee_earner: feeEarner,
      },
      tasks,
    };
    try {
      let res;
      const headers = {};
      const bonsaiUserId = sessionStorage.getItem("bonsai_user_id");
      if (bonsaiUserId) headers["X-Bonsai-User-Id"] = bonsaiUserId;

      if (existingId) {
        res = await axios.put(`${API}/postcompletion/${existingId}`, payload, { headers });
      } else {
        res = await axios.post(`${API}/postcompletion`, payload, { headers });
      }
      const todoSync = res.data.todo_sync;
      let msg = `Checklist ${existingId ? "updated" : "saved"} — ${res.data.status}`;
      if (todoSync?.synced > 0) msg += ` · ${todoSync.synced} task(s) synced to To Do`;
      if (todoSync?.failed > 0) msg += ` · ${todoSync.failed} To Do sync(s) failed`;
      toast.success(msg);
      if (todoSync?.errors?.length && todoSync.errors[0].includes("Microsoft account")) {
        toast.info("Link your Microsoft account to enable To Do sync", { duration: 5000 });
      }
      navigate(`/submission/${res.data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.validation_errors) {
        setErrors(detail.validation_errors);
      } else {
        setErrors([errorMessage(err, "Failed to save.")]);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {errors.length > 0 && (
        <div className="glass-card p-4 border-red-500/30">
          <div className="flex items-center gap-2 text-status-red font-medium mb-1">
            <AlertCircle className="h-4 w-4" /> Validation Errors
          </div>
          <ul className="list-disc list-inside text-sm text-status-red space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Header */}
      <div className="glass-card p-5">
        <h3 className="text-sm font-bold text-teal mb-4">Header</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm font-medium text-txt">Matter Reference</Label>
            <Input value={matterReference} onChange={(e) => setMatterReference(e.target.value)} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" data-testid="pc-matter-ref" />
          </div>
          <div>
            <Label className="text-sm font-medium text-txt">Client Name</Label>
            <Input value={clientName} onChange={(e) => setClientName(e.target.value)} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" data-testid="pc-client-name" />
          </div>
          <div>
            <Label className="text-sm font-medium text-txt">Counterparty</Label>
            <Input value={counterpartyName} onChange={(e) => setCounterpartyName(e.target.value)} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" data-testid="pc-counterparty" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label className="text-sm font-medium text-txt">Transaction Type</Label>
            <Input value={transactionType} onChange={(e) => setTransactionType(e.target.value)} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" data-testid="pc-transaction-type" />
          </div>
          <div>
            <Label className="text-sm font-medium text-txt">Completion Date</Label>
            <Input type="date" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white" data-testid="pc-completion-date" />
          </div>
          <div>
            <Label className="text-sm font-medium text-txt">Fee Earner</Label>
            <Input value={feeEarner} onChange={(e) => setFeeEarner(e.target.value)} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" data-testid="pc-fee-earner" />
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-sm font-medium text-txt">Matter Description</Label>
          <Input value={matterDescription} onChange={(e) => setMatterDescription(e.target.value)} className="bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" data-testid="pc-matter-desc" />
        </div>
      </div>

      {/* Tasks */}
      <div className="glass-card p-5">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-sm font-bold text-teal">Tasks</h3>
            <p className="text-xs text-txt-muted mt-0.5">
              {completedCount} of {totalCount} completed
              {allDone && totalCount > 0 && <span className="ml-2 text-teal font-medium">All done!</span>}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={markAllComplete} disabled={allDone || totalCount === 0} className="border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white" data-testid="pc-mark-all-complete">
              <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark All
            </Button>
            <Button variant="outline" size="sm" onClick={addTask} className="border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white" data-testid="pc-add-task">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Task
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        {totalCount > 0 && (
          <div className="mb-4">
            <div className="w-full h-2 bg-navy-hover rounded-md overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-teal to-teal rounded-md transition-all duration-500"
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
                data-testid="pc-progress-bar"
              />
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid="pc-tasks-table">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-txt-muted border-b border-navy-border">
                <th className="p-2 w-8"></th>
                <th className="p-2 w-28">Category</th>
                <th className="p-2">Description</th>
                <th className="p-2 w-28">Due Date</th>
                <th className="p-2 w-28">Responsible</th>
                <th className="p-2 w-32">Notes</th>
                <th className="p-2 w-16 text-center">To Do</th>
                <th className="p-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task, idx) => (
                <tr key={task.id} className={`border-b border-navy-border/40 hover:bg-navy-hover/30 transition-colors ${task.completed ? "opacity-50" : ""}`}>
                  <td className="p-2 text-center">
                    <Checkbox
                      checked={task.completed}
                      onCheckedChange={(v) => updateTask(idx, "completed", !!v)}
                      data-testid={`pc-task-check-${idx}`}
                    />
                  </td>
                  <td className="p-2">
                    <Input value={task.category} onChange={(e) => updateTask(idx, "category", e.target.value)} className="text-xs bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white" data-testid={`pc-task-cat-${idx}`} />
                  </td>
                  <td className="p-2">
                    <Input value={task.description} onChange={(e) => updateTask(idx, "description", e.target.value)} className={`text-xs bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white ${task.completed ? "line-through" : ""}`} data-testid={`pc-task-desc-${idx}`} />
                  </td>
                  <td className="p-2">
                    <Input type="date" value={task.due_date} onChange={(e) => updateTask(idx, "due_date", e.target.value)} className="text-xs bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white" data-testid={`pc-task-due-${idx}`} />
                  </td>
                  <td className="p-2">
                    <Input value={task.responsible} onChange={(e) => updateTask(idx, "responsible", e.target.value)} className="text-xs bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white" data-testid={`pc-task-resp-${idx}`} />
                  </td>
                  <td className="p-2">
                    <Input value={task.notes} onChange={(e) => updateTask(idx, "notes", e.target.value)} className="text-xs bg-[rgba(26,37,71,0.85)] border-[rgba(99,102,241,0.3)] text-white placeholder:text-txt-muted" placeholder="Notes..." data-testid={`pc-task-notes-${idx}`} />
                  </td>
                  <td className="p-2 text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      <Checkbox
                        checked={task.sync_to_todo || false}
                        onCheckedChange={(v) => updateTask(idx, "sync_to_todo", !!v)}
                        data-testid={`pc-task-todo-${idx}`}
                      />
                      {task.todo_task_id && (
                        <span className="text-[10px] text-teal font-medium leading-none" data-testid={`pc-task-synced-${idx}`}>Synced</span>
                      )}
                    </div>
                  </td>
                  <td className="p-2">
                    <Button variant="ghost" size="sm" onClick={() => removeTask(idx)} className="text-status-red/60 hover:text-status-red">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {tasks.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-txt-muted italic">No tasks. Click "Add Task" to begin.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={onBack} className="border-navy-border text-txt-muted hover:bg-navy-hover hover:text-white" data-testid="pc-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <Button onClick={handleSave} disabled={saving} className="bg-teal hover:bg-teal-hover text-white gap-2 px-8" data-testid="pc-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving..." : existingId ? "Update Checklist" : "Save Checklist"}
        </Button>
      </div>
    </div>
  );
}

// ========== Main Page ==========

export default function PostCompletionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const existingId = searchParams.get("id");

  useEffect(() => {
    if (existingId) setStep(2);
    const mid = searchParams.get("matter_id");
    if (mid && !existingId) {
      axios.get(`${API}/submissions/${mid}`).then((res) => {
        const sub = res.data;
        setSelectedMatter({
          ...sub,
          matter_reference: sub.matter_reference || sub.data?.Q4 || sub.data?.Q3 || "",
          secondary_label: sub.secondary_label || sub.matter_reference || sub.data?.Q4 || "",
          primary_label: sub.primary_label || sub.data?.Q1 || "",
        });
        setStep(2);
      }).catch(() => {});
    }
  }, [searchParams, existingId]);

  return (
    <div className="min-h-screen bg-navy">
      <nav className="sticky top-0 z-30 glass-nav border-b border-[rgba(99,102,241,0.25)]">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai" className="h-7" />
            <span className="text-white font-bold text-sm">Post-Completion Checklist</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-txt-muted hover:text-teal" data-testid="pc-back-dashboard">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Dashboard
          </Button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-8 animate-fade-in-up">
        {!existingId && (
          <div className="flex items-center gap-3 mb-8">
            <StepBubble num={1} label="Select Matter" active={step === 1} done={step > 1} />
            <div className="h-px flex-1 bg-navy-hover" />
            <StepBubble num={2} label="Checklist" active={step === 2} done={false} />
          </div>
        )}

        {step === 1 && <StepMatterSelection onSelect={(m) => { setSelectedMatter(m); setStep(2); }} />}
        {step === 2 && <StepChecklist initialMatter={selectedMatter} onBack={() => existingId ? navigate(-1) : setStep(1)} existingId={existingId} />}
      </main>
    </div>
  );
}

function StepBubble({ num, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold ${done ? "bg-teal text-white" : active ? "bg-teal text-white" : "bg-navy-hover text-txt-muted"}`}>
        {done ? <CheckCircle2 className="w-4 h-4" /> : num}
      </div>
      <span className={`text-sm ${active || done ? "font-medium text-white" : "text-txt-muted"}`}>{label}</span>
    </div>
  );
}
